'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'
import Cookies from 'js-cookie'
import { trackEvent, getCommonEventProperties } from '@acko-tech/building-blocks.ui.acko-analytics'
import HeaderDesktopAndMweb from '@components/Product/ProductNav/headerDesktopAndMweb'
import Award from '@components/Home/Hero/Award'
import { useUserContext } from '@context/UserContext/UserContenxt'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { callWebviewHandler, WebViewActions } from '@utils/webview'
import { genericWebviewHandler, WebViewActions as ClientWebViewActions } from '@utils/client/webview'
import { getUrlWithQueryParams } from '@utils/queryParamUtils'
import { parseBoldText } from '@utils/parseBoldText'
import { getUrgentRenewalCardsByProduct, getContinueInsuringCardsByProduct, formatPremium } from '@utils/giCar/utils'
import { RecentSearchData, RenewalPlanData } from '@interfaces/giCar'
import ContinueInsureCard from '@components/GICar/giCarWidgets/ContinueInsureCard'
import UrgentRenewalCard from '@components/GICar/giCarWidgets/UrgentRenewalCard'
import RegistrationNumber from './RegistrationNumber'
import {
  ANALYTICS,
  CONSTANTS,
  DEFAULTS,
  ERROR_MESSAGES,
  FORM,
  IMAGES,
  QUERY_PARAMS,
  TEXT,
  URLS,
} from './config'
import styles from './GIBikeLanding.module.scss'
import ExploreNewCard from './ExploreNewCard'

interface GIBikeLandingPageProps {
  mobileNumber?: string
  renewalPlans?: RenewalPlanData[]
  recentSearches?: RecentSearchData[]
  isMobile?: boolean
  regexPatternsData?: string[]
}

const getDefaultPlanRedirectUrl = (plan?: RenewalPlanData) => {
  const defaultPlan = plan?.plans?.find?.(item => item?.default_plan)
  return defaultPlan?.redirect_url || plan?.redirect_url || ''
}

const GIBikeLandingPage: React.FC<GIBikeLandingPageProps> = props => {
  const isMobile = props.isMobile ?? false
  const renewalPlans = props.renewalPlans ?? []
  const regexPatternsData = props.regexPatternsData ?? []
  const searchParams = useSearchParams()
  const { userData } = useUserContext()
  const formContainerRef = useRef<HTMLDivElement>(null)
  const [appBarHeight, setAppBarHeight] = useState(0)
  const mobileNumber = userData?.phone || props.mobileNumber || ''
  const urlBikeNumber =
    searchParams.get(QUERY_PARAMS.BIKE_NUMBER) || searchParams.get(QUERY_PARAMS.LEGACY_BIKE_NUMBER) || ''

  const urgentCards = getUrgentRenewalCardsByProduct(renewalPlans, 'bike')
  const continueCards = getContinueInsuringCardsByProduct(props.recentSearches || [], 'bike')
  const totalMobileCards = urgentCards.length + continueCards.length
  const isSingleMobileCard = totalMobileCards === 1

  const radius = CONSTANTS.CIRCLE_RADIUS
  const circumference = 2 * Math.PI * radius
  const topOffset = -(circumference / 4)

  useEffect(() => {
    if (!Cookies.get('webview') || !(window as any).flutter_inappwebview?.callHandler) return

    void genericWebviewHandler({
      action: ClientWebViewActions.UPDATE_APP_BAR,
      actionValue: {
        is_appbar_transparent: true,
        back_button_color: '#000000',
        status_icon_brightness: 'dark',
      },
    })
    console.log('header height:', Cookies.get('native_header_height'))
    const appBarHeightCookie = Cookies.get('native_header_height') || '64'
    const height = Number(appBarHeightCookie)

    if (!Number.isNaN(height) && height > 0) {
      setAppBarHeight(height)
    }
  }, [])

  const handleRenewalClick = (planOrProposalId?: RenewalPlanData | string) => {
    void (async () => {
      const plan = typeof planOrProposalId === 'string' ? undefined : planOrProposalId
      const proposalId = typeof planOrProposalId === 'string' ? planOrProposalId : planOrProposalId?.proposal_id
      const redirectUrl = getDefaultPlanRedirectUrl(plan)

      try {
        await trackEvent(ANALYTICS.EVENTS.TAP_RENEWAL_CARD, {
          ...getCommonEventProperties('seo'),
          page: typeof window !== 'undefined' ? window.location.href : '',
          from_page: typeof document !== 'undefined' ? document.referrer : '',
          header_name: ANALYTICS.HEADER_NAMES,
          product: ANALYTICS.PRODUCT.AUTO_BIKE,
          funnel: ANALYTICS.FUNNEL.RENEWAL_ENTRY,
          action_type: 'Renew',
          title: ANALYTICS.PAGE_TITLE,
          proposal_id: proposalId || '',
        })
      } catch (error) {
        console.error('Failed to track tap_renewal_card event:', error)
      }

      if (redirectUrl) {
        window.location.href = getUrlWithQueryParams(redirectUrl)
        return
      }

      console.error(ERROR_MESSAGES.NO_RENEWAL_REDIRECT_URL, { proposal_id: proposalId || '' })
    })()
  }

  const handleContinueInsureClick = (item?: RecentSearchData) => {
    const proposalId = item?.id || item?.proposal_id
    const continueUrl = item?.redirect_link

    if (!continueUrl) {
      console.error(ERROR_MESSAGES.NO_REDIRECT_LINK)
      return
    }

    void (async () => {
      try {
        await trackEvent(ANALYTICS.EVENTS.TAP_CONTINUE_INSURE_CARD, {
          ...getCommonEventProperties('seo'),
          page: typeof window !== 'undefined' ? window.location.href : '',
          from_page: typeof document !== 'undefined' ? document.referrer : '',
          header_name: ANALYTICS.HEADER_NAMES,
          product: ANALYTICS.PRODUCT.AUTO_BIKE,
          funnel: ANALYTICS.FUNNEL.CONTINUE_ENTRY,
          action_type: 'Continue',
          title: ANALYTICS.PAGE_TITLE,
          proposal_id: proposalId || '',
          registration_number: item?.registration_number || '',
        })
      } catch (error) {
        console.error('Failed to track tap_continue_insure_card event:', error)
      }

      window.location.href = getUrlWithQueryParams(continueUrl)
    })()
  }

  const handleBackClick = () => {
    if (typeof window === 'undefined') return
    if (Cookies.get('webview')) {
      callWebviewHandler({ action: WebViewActions.CLOSE_WEBVIEW })
    } else {
      window.location.href = getUrlWithQueryParams(URLS.HOME)
    }
  }

  const handleMainScrollWheel = (event: React.WheelEvent<HTMLElement>) => {
    if (typeof window === 'undefined' || window.innerWidth <= CONSTANTS.DESKTOP_BREAKPOINT) return
    const container = formContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const canScrollDown = scrollTop + clientHeight < scrollHeight - 1
    const canScrollUp = scrollTop > 0

    if ((event.deltaY > 0 && canScrollDown) || (event.deltaY < 0 && canScrollUp)) {
      event.preventDefault()
      container.scrollTop += event.deltaY
    }
  }

  return (
    <>
      <div className={styles.hideOnMobile}>
        <HeaderDesktopAndMweb isMobile={isMobile} />
      </div>
      <div className={styles.pageContainer}>
        <main
          className={styles.mainContent}
          style={{ ['--appBarHeight' as any]: appBarHeight }}
          onWheel={handleMainScrollWheel}>
          <div className={styles.mobileHeroSection}>
            <PropertyControlledComponent controllerProperty={appBarHeight === 0} fallback={null}>
              <button type="button" className={styles.mobileBackButton} onClick={handleBackClick} aria-label="Go back">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="#1a1a1a"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </PropertyControlledComponent>
            <div className={styles.mobileHero}>
              <h1 className={styles.headingLine}>{parseBoldText(TEXT.HEADING, styles.highlight)}</h1>
              <Image {...IMAGES.BIKE_MOBILE} priority />
            </div>

            <PropertyControlledComponent controllerProperty={totalMobileCards > 0} fallback={null}>
              <div
                className={styles.mobileCardsCarousel}
                style={isSingleMobileCard ? { justifyContent: 'flex-start', overflowX: 'hidden' } : undefined}>
                {urgentCards.map(plan => (
                  <UrgentRenewalCard
                    key={
                      plan?.proposal_id ||
                      `${plan?.mmv?.make || plan?.vehicle_details?.make || ''} ${plan?.mmv?.model || plan?.vehicle_details?.model || ''
                      }`
                    }
                    plan={plan}
                    onClick={handleRenewalClick}
                    style={isSingleMobileCard ? { width: '100%', maxWidth: '100%' } : undefined}
                    circumference={circumference}
                    topOffset={topOffset}
                    RENEWAL_THRESHOLD_DAYS={CONSTANTS.RENEWAL_THRESHOLD_DAYS}
                    fallbackVehicleName="Bike"
                  />
                ))}
                {continueCards.map((item, index) => (
                  <ContinueInsureCard
                    key={item?.redirect_link || item?.proposal_id || index}
                    item={item}
                    onClick={handleContinueInsureClick}
                    style={isSingleMobileCard ? { width: '100%', maxWidth: '100%' } : undefined}
                    formatPremium={formatPremium}
                    index={index}
                    vehicleTypeLabel="bike"
                  />
                ))}
              </div>
            </PropertyControlledComponent>

            <div className={styles.mobileRegistrationCard}>
              <h2 className={styles.formLabel}>{TEXT.FORM_LABEL}</h2>
              <RegistrationNumber
                mobileNumber={mobileNumber}
                autoFillNumber={urlBikeNumber}
                autoSubmit={DEFAULTS.AUTO_SUBMIT}
                regexPatternsData={regexPatternsData}
                formData={FORM}
                headerName={ANALYTICS.HEADER_NAMES}
              />
            </div>
          </div>

          <div className={`${styles.heroContainer} ${styles.hideOnMobile}`}>
            <div className={styles.trustBadges}>
              {TEXT.AWARDS.map((award, index) => (
                <Award key={index}>{award}</Award>
              ))}
            </div>

            <h1 className={styles.headingLine}>{parseBoldText(TEXT.HEADING, styles.highlight)}</h1>

            <div className={styles.carImageContainer}>
              <Image {...IMAGES.BIKE_DESKTOP} priority className={styles.carImage} />
            </div>
          </div>

          <div className={styles.formContainer} ref={formContainerRef}>
            <PropertyControlledComponent controllerProperty={urgentCards.length > 0} fallback={null}>
              {urgentCards.map(plan => (
                <UrgentRenewalCard
                  key={
                    plan?.proposal_id ||
                    `${plan?.mmv?.make || plan?.vehicle_details?.make || ''} ${plan?.mmv?.model || plan?.vehicle_details?.model || ''
                    }`
                  }
                  plan={plan}
                  onClick={handleRenewalClick}
                  className={styles.hideOnMobile}
                  circumference={circumference}
                  topOffset={topOffset}
                  RENEWAL_THRESHOLD_DAYS={CONSTANTS.RENEWAL_THRESHOLD_DAYS}
                  fallbackVehicleName="Bike"
                />
              ))}
            </PropertyControlledComponent>
            <PropertyControlledComponent controllerProperty={continueCards.length > 0} fallback={null}>
              {continueCards.map((item, index) => (
                <ContinueInsureCard
                  key={item?.redirect_link || item?.proposal_id || index}
                  item={item}
                  onClick={handleContinueInsureClick}
                  className={styles.hideOnMobile}
                  formatPremium={formatPremium}
                  index={index}
                  vehicleTypeLabel="bike"
                />
              ))}
            </PropertyControlledComponent>

            <div className={`${styles.registrationCard} ${styles.hideOnMobile}`}>
              <div className={styles.registrationCardContent}>
                <h2 className={styles.formLabel}>{TEXT.FORM_LABEL}</h2>
                <RegistrationNumber
                  mobileNumber={mobileNumber}
                  autoFillNumber={urlBikeNumber}
                  autoSubmit={DEFAULTS.AUTO_SUBMIT}
                  regexPatternsData={regexPatternsData}
                  formData={FORM}
                  headerName={ANALYTICS.HEADER_NAMES}
                />
              </div>
            </div>
            <ExploreNewCard />
          </div>
        </main>

        {/* <div className={styles.videoContainer}>
          <TrustHomeVideoPlayers videoPlayerData={[VIDEO_DATA]} />
        </div> */}
      </div>
    </>
  )
}

export default GIBikeLandingPage
